import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { CustomerPayment } from '../../../../../../../models/customer';
import {
  canChangePaymentCustomer,
  canRefundPayment,
  getPaymentMethodTypeDisplay,
  isPOSPayment,
} from '../../../../../../../models/payment';
import { AccountService } from '../../../../../../../services/account/account.service';
import { CustomerService } from '../../../../../../../services/customer/customer.service';
import { dateStringFromISO8601 } from '../../../../../../../utils';
import { OrganizationConfigService } from '../../../../../../../services/organization-config/organization-config.service';
import { PaymentService } from '../../../../../../../services/payment/payment.service';
import { LocalStorage } from 'angular-web-storage';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ListResponse } from '../../../../../../../models/list-response';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { RefundPaymentModalComponent } from '../../../../payments/modals/refund-payment-modal/refund-payment-modal.component';
import { PaymentDetailsModalComponent } from '../../../../payments/modals/payment-details-modal/payment-details-modal.component';
import {
  ChangePaymentCustomerModalComponent,
} from '../../../../payments/modals/change-payment-customer-modal/change-payment-customer-modal.component';

import { TaxDisplayFormat } from '../../../../../../../models/organization-config-enums';
import { PaymentMethodType, PaymentStatus, PaymentType } from '../../../../../../../models/payment-enums';

@Component({
  selector: 'app-payments-sub-list',
  templateUrl: './payments-sub-list.component.html',
  styleUrls: ['./payments-sub-list.component.css']
})
export class PaymentsSubListComponent implements OnInit {

  public pageNumber = 1;
  // Giving this a project-unique name ensures it is not shared with other saved
  // pageSize variables.
  @LocalStorage() public pageSizeCustomerPaymentsSubList;

  public filtersChangedSubject = new Subject<null>();

  public customerId: number;

  public paymentsList: ListResponse<CustomerPayment>;
  public loadingPayments = false;
  public loadingDownload = false;

  public customerNotFound = false;
  public successMessage = '';
  public errorMessage = '';
  public serverError: number = null;

  // Make relevant enums and library functions available to the template.
  public readonly PaymentType = PaymentType;
  public readonly PaymentMethodType = PaymentMethodType;
  public readonly PaymentStatus = PaymentStatus;
  public readonly TaxDisplayFormat = TaxDisplayFormat;
  public readonly isPOSPayment = isPOSPayment;
  public readonly canRefundPayment = canRefundPayment;
  public readonly canChangePaymentCustomer = canChangePaymentCustomer;
  public readonly dateStringFromISO8601 = dateStringFromISO8601;
  public readonly getPaymentMethodTypeDisplay = getPaymentMethodTypeDisplay;

  // The number of list requests. Used to ensure that only the most recent
  // request is displayed, in the event of concurrent requests (due to,
  // e.g., filtering).
  private listRequestCount = 0;

  constructor(
    public accountService: AccountService,
    public orgConfigService: OrganizationConfigService,
    private route: ActivatedRoute,
    private customerService: CustomerService,
    private paymentService: PaymentService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_members) {
      return;
    }
    const routeParams = this.route.parent.snapshot.params;
    this.customerId = parseInt(routeParams.id, 10);
    if (isNaN(this.customerId)) {
      this.customerNotFound = true;
      return;
    }

    if (!this.pageSizeCustomerPaymentsSubList) {
      this.pageSizeCustomerPaymentsSubList = 10;
    }
    // This is needed in order for the pageSize to be updated when the html
    // select is first changed.
    // noinspection SillyAssignmentJS
    this.pageSizeCustomerPaymentsSubList = this.pageSizeCustomerPaymentsSubList;

    // Handle changes to the filters by reloading the list, subject to debounce.
    this.filtersChangedSubject.pipe(
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadPayments();
    });

    this.loadPayments();
  }

  public downloadReceipt(payment: CustomerPayment) {
    this.loadingDownload = true;

    this.successMessage = '';
    this.errorMessage = '';

    this.paymentService
      .downloadReceipt(payment.id, payment.invoice_id)
      .then(() => {
        this.successMessage = 'Receipt downloaded successfully.';
      })
      .catch(reason => {
        if (reason.status === 400) {
          reason.error.text().then(text => { this.errorMessage = text; });
        } else if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to download this receipt.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingDownload = false; });
  }

  /**
   * Update the current page number to the given value and load the page of
   * results.
   *
   * @param newPageNumber - The new page number.
   */
  public setPage(newPageNumber: number) {
    this.pageNumber = newPageNumber;
    this.filtersChangedSubject.next();
  }

  /**
   * Get the number of the last page of results.
   */
  public lastPageNumber() {
    if (!this.paymentsList) { return 1; }
    return Math.ceil(this.paymentsList.count / this.pageSizeCustomerPaymentsSubList);
  }

  public openRefundPaymentModal(payment: CustomerPayment) {
    const modalRef = this.modalService.open(RefundPaymentModalComponent);
    modalRef.componentInstance.payment = payment;
    modalRef.componentInstance.successCallback = () => {
      this.loadPayments();
      this.successMessage = "Payment refund in progress. The refund may take multiple days to reach the customer's account.";
    };
  }

  public openChangePaymentCustomerModal(payment: CustomerPayment) {
    const modalRef = this.modalService.open(ChangePaymentCustomerModalComponent);
    modalRef.componentInstance.paymentId = payment.id;
    modalRef.componentInstance.savedCallback = (successMessage: string) => {
      this.successMessage = successMessage;
      this.loadPayments();
    };
  }

  public openPaymentDetailsModal(payment: CustomerPayment) {
    const modalRef = this.modalService.open(PaymentDetailsModalComponent);
    const instance: PaymentDetailsModalComponent = modalRef.componentInstance;
    instance.paymentId = payment.id;
    instance.onDownloadReceipt = p => this.downloadReceipt(p);
    instance.onViewRefundOptions = p => this.openRefundPaymentModal(p);
  }

  private loadPayments() {
    this.loadingPayments = true;

    // Increment the request count and save a local copy.
    this.listRequestCount++;
    const thisRequestCount = this.listRequestCount;

    this.customerService
      .getCustomerPayments(
        this.customerId,
        this.pageNumber,
        this.pageSizeCustomerPaymentsSubList
      )
      .then(results => {
        // Only update the list if there has not been a more recent request
        // initiated.
        if (thisRequestCount === this.listRequestCount) {
          this.paymentsList = results;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.customerNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.listRequestCount) {
          this.loadingPayments = false;
        }
      });
  }
}
